import { CameraIcon } from '@heroicons/react/24/solid'
import { type MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'
import alexChernenko from './alex-chernenko-inqfXtFueYQ-unsplash.jpg'

export const meta: MetaFunction = () => [
	{ title: 'Auto Insurance' },
	{
		name: 'description',
		content: 'Auto insurance coverage overview.',
	},
]

const GlossaryItemPage = () => (
	<main className="mx-auto my-10 max-w-7xl">
		<Link to="/" prefetch="intent" className="text-blue-600 underline">
			Home
		</Link>{' '}
		&rarr;{' '}
		<Link
			to="/products-and-programs"
			prefetch="intent"
			className="text-blue-600 underline"
		>
			Products and Programs
		</Link>
		<div className="w-full overflow-hidden bg-white">
			<div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
				<div className="absolute bottom-0 left-3/4 top-0 hidden w-screen bg-gray-50 lg:block" />
				<div className="mx-auto max-w-prose text-base lg:grid lg:max-w-none lg:grid-cols-2 lg:gap-8">
					<div>
						<h2 className="text-base font-semibold uppercase tracking-wide text-indigo-600">
							Coverage Overview
						</h2>
						<h3 className="mt-2 text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
							Auto Insurance
						</h3>
					</div>
				</div>
				<div className="mt-8 lg:grid lg:grid-cols-2 lg:gap-8">
					<div className="relative lg:col-start-2 lg:row-start-1">
						<svg
							className="absolute right-0 top-0 -mr-20 -mt-20 hidden lg:block"
							width={404}
							height={384}
							fill="none"
							viewBox="0 0 404 384"
							aria-hidden="true"
						>
							<defs>
								<pattern
									id="de316486-4a29-4312-bdfc-fbce2132a2c1"
									x={0}
									y={0}
									width={20}
									height={20}
									patternUnits="userSpaceOnUse"
								>
									<rect
										x={0}
										y={0}
										width={4}
										height={4}
										className="text-gray-200"
										fill="currentColor"
									/>
								</pattern>
							</defs>
							<rect
								width={404}
								height={384}
								fill="url(#de316486-4a29-4312-bdfc-fbce2132a2c1)"
							/>
						</svg>
						<div className="relative mx-auto max-w-prose text-base lg:max-w-none">
							<figure>
								<div className="aspect-w-12 aspect-h-7 lg:aspect-none">
									<img
										className="rounded-lg object-cover object-center shadow-lg"
										src={alexChernenko}
										alt="Driving through the summer"
										width={1184}
										height={1376}
									/>
								</div>
								<figcaption className="mt-3 flex text-sm text-gray-500">
									<CameraIcon
										className="h-5 w-5 flex-none text-gray-400"
										aria-hidden="true"
									/>
									<span className="ml-2">Photograph by Alex Chernenko</span>
								</figcaption>
							</figure>
						</div>
					</div>
					<div className="mt-8 lg:mt-0">
						<div className="prose prose-indigo mx-auto mt-5 text-lg text-gray-500 lg:col-start-1 lg:row-start-1 lg:max-w-none">
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								There are three main categories of coverage on the auto
								insurance policy. They will be discussed below in the following
								three sections:
								<ol>
									<li>Liability</li>
									<li>Medical</li>
									<li>Your Vehicle</li>
								</ol>
							</p>
							<h2 className="mt-2 text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
								Liability
							</h2>
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								Automobile liability insurance protects a policyholder from
								financial loss arising from legal liability incurred when his or
								her vehicle causes damage to the property of others or injury to
								someone. The design of auto liability coverage has a single
								limit of liability for both bodily injury and property damage,
								but many carriers offer a separate limit for bodily injury and
								property damage.
							</p>
							<h3>Bodily Injury Liability</h3>
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								With a split liability limit, the first limit is the most the
								policy will pay for injuries to any one person; the second limit
								is the most the policy will pay for the injuries of two or more
								persons injured in a single accident. With a single limit of
								liability, the coverage limit may be applied to both bodily
								injury and property damage liability.
							</p>
							<h3>Property Damage Liability</h3>
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								This liability coverage part pays your legal limit of liability
								for the damage of property of others, subject to a maximum limit
								per accident.
							</p>
							<h2 className="mt-2 text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
								Medical
							</h2>
							<h3>Medical Payments</h3>
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								This coverage pays all necessary medical and funeral expenses
								incurred and services rendered to the occupants of a vehicle
								that is involved in an accident. It can be used when other
								coverages do not apply, for example if the accident was caused
								as a result of drunk driving and the PIP coverage is denied.
							</p>
							<h3 id="personal-injury-protection">
								Personal Injury Protection (PIP)
							</h3>
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								All NY State auto policies carry a minimum of $50,000 in PIP
								coverage. Most policies offer the option to increase this to a
								higher coverage limit. It is a first-party coverage (meaning
								that your own policy pays for your necessary expenses) for
								medical, hospital, nursing, surgical, ambulance, dental, x-ray,
								prescription drug, and prosthetic services; psychiatric,
								physical, and occupational therapy and rehabilitation; and any
								other professional health services.
							</p>
							<h3>Optional Basic Economic Loss (OBEL)</h3>
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								As the name suggests, this is an optional coverage available on
								the personal auto policy. OBEL has a limit of $25,000 which can
								be applied at the discretion of the injured passengers of the
								vehicle as a result of an accident. In addition to covering the
								same category of medical costs included in the PIP section
								above, it can be used to cover lost wages and physical therapy.
							</p>
							<h3>Uninsured/Underinsured Motorist</h3>
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								This coverage part pays for damages that the policyholder is
								legally entitled to recover from a negligent owner of an
								uninsured/underinsured vehicle. This does not include exemplary
								or punitive damages. Medical expenses, lost wages, and other
								general damages incurred as a result of an accident caused by an
								uninsured/underinsured motorist. This coverage does not include
								property damage.
							</p>
							<h2 className="mt-2 text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
								Your Vehicle
							</h2>
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								Physical damage coverage is an optional coverage on the auto
								policy. There are two categories of loss to your vehicle that
								are insured separately. They are commonly referred to as
								"Collision" coverage and "Comprehensive" coverage, also known as
								"Other than Collision." There are also coverage parts offered in
								conjunction with physical damage coverage that are aimed at
								minimizing the impact of a loss.
							</p>
							<h3>Collision</h3>
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								Collision covers damage to your vehicle as a result of upset or
								impact of your vehicle with another vehicle or object. It is
								subject to a deductible.
							</p>
							<h3>Comprehensive</h3>
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								Comprehensive coverage steps in to reduce the cost of
								replacement or repair of your vehicle when loss is sustained by
								fire, theft, vandalism, hail, deer, or many other types of
								damage caused by something other than a collision. This includes
								coverage for breakage of glass.  This coverage part has a
								deductible.
							</p>
							<h3>Full Coverage for Window Glass</h3>
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								Coverage for window glass on your vehicle is covered under the
								comprehensive section. On a personal automobile policy, this
								coverage may be available without a deductible. This is called
								"full glass" coverage. On a commercial policy, if full glass is
								not offered by a particular insurer, we can sometimes accomplish
								the same outcome by selecting a zero deductible on
								comprehensive.
							</p>
							<h3>Rental Reimbursement</h3>
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								This coverage will reimburse the policyholder for rental charges
								incurred when a covered vehicle is out of use as a result of a
								covered loss.
							</p>
							<h3>Towing</h3>
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								This coverage may be available for purchase in addition to
								physical damage coverage. It can reduce or eliminate the cost of
								transporting a vehicle from where it is disabled in an accident.
								This coverage on some policies may also cover towing for a
								vehicle that becomes disabled without being in an accident.
							</p>
							<hr />
							<p
								data-paragraph
								className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
							>
								Disclaimer: Please refer to your individual policy declarations
								and definitions for the insurance policy in question. Glossary
								terms are offered as a service to the visitors of our website
								and are not a guarantee of coverage or claim outcome. While we
								do our best to provide clear and accurate coverage descriptions,
								your individual policy and the company and agent who provided it
								are the best resource for exact contractual obligations of the
								insurance company.
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</main>
)

export default GlossaryItemPage
